import axios from "axios";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { logger } from "@/infrastructure/services/LoggerService";
import { isConsentRelevantChannel, toConsentChannelType, } from "@/modules/contact/domain/entities/contactConsent";
import { getContactConsents } from "@/modules/contact/infrastructure/adapters/contactConsentService";
import { listedPurposeCodes, toDisplayedPurposes } from "../domain/entities/consentOverview";
import { purposeKindOf } from "../domain/entities/consentPurpose";
import { getConsentMatrix } from "../infrastructure/adapters/consentMatrixService";
import { listConsentPurposes } from "../infrastructure/adapters/consentPurposeService";
import { resolveConsentPurposeLabel } from "./useConsentPurposeLabel";
const LABEL_KEY_PREFIX = "contact.detail.consentsWidget.channel";
/**
 * Qualifier label key for an address, derived from the contact channel. The consent read
 * carries no qualifier (only channelType / value / normalizedValue / isPrimary), so the
 * "Email personnel" vs "Email entreprise" distinction has to come from the contact aggregate.
 */
export function labelKeyFor(channel) {
    const base = channel.type === "Phone" ? "phone" : "email";
    if (channel.usage === "Personal")
        return `${LABEL_KEY_PREFIX}.${base}Personal`;
    if (channel.usage === "Professional")
        return `${LABEL_KEY_PREFIX}.${base}Professional`;
    return `${LABEL_KEY_PREFIX}.${base}`;
}
/**
 * Read-only consent overview for one contact, shaped for the record-view widget.
 *
 * The matrix and purpose-catalogue reads go through their adapters directly rather than
 * through `useConsentMatrix`: that composable targets the admin configuration view (toggle /
 * save / dirty tracking, toasts) and collapses a 403 into a generic load error, which would
 * show an error banner to every non-admin user instead of hiding the widget.
 *
 * The union/mapping logic below still mirrors `ContactConsentsTab.vue`'s `groups` computed: the
 * matrix-active map, the purpose-label fallback and the active ∪ decided union are written twice.
 * Only the badge display rule is shared so far (`toDisplayedPurposes`). Nothing prevents sharing
 * the rest — both sides produce the same unfiltered list and only differ in what they do with it —
 * it simply has not been extracted yet, and until it is, a change to the union or to either
 * fallback key has to be made in both places or the two screens drift apart.
 *
 * @param contactIdGetter A getter, not a plain string: the consuming widget holds one
 *   composable instance across contact navigations and calls `load()` again from a `watch` on
 *   the route/contact id. A captured string would keep querying the ORIGINAL contact forever.
 *   Read it fresh at the top of every `load()`, exactly like `channelsGetter`.
 */
export function useContactConsentsOverview(contactIdGetter, channelsGetter) {
    const { t, locale } = useI18n();
    const addresses = ref([]);
    const isLoading = ref(false);
    const loadError = ref(false);
    const isForbidden = ref(false);
    // Monotonic call token (R97 shape): only the LATEST in-flight `load()` may write state.
    // Two overlapping calls (e.g. a fast double-click retry) can settle out of order; without
    // this guard the loser's result/error clobbers the winner's already-rendered state.
    let requestId = 0;
    function consentChannels(channels) {
        return channels.map((channel) => ({
            channelType: channel.type,
            value: channel.value,
            isPrimary: channel.isPrimary,
        }));
    }
    async function load() {
        const currentRequestId = ++requestId;
        const contactId = contactIdGetter();
        // Snapshot once: the same filtered array feeds both the request payload and the
        // post-await join, so a channel change mid-flight can't desync the two.
        const contactChannels = channelsGetter().filter(isConsentRelevantChannel);
        const channels = consentChannels(contactChannels);
        addresses.value = [];
        loadError.value = false;
        isForbidden.value = false;
        if (channels.length === 0) {
            // This call owns requestId (just incremented), so it also owns clearing the flag: an
            // older in-flight call's gated `finally` will refuse to, and the widget would otherwise
            // sit on its loading skeleton forever.
            isLoading.value = false;
            return;
        }
        isLoading.value = true;
        try {
            const [consents, matrix, catalogue] = await Promise.all([
                getContactConsents(contactId, channels),
                getConsentMatrix(),
                listConsentPurposes(),
            ]);
            if (currentRequestId !== requestId)
                return;
            // Active matrix purposes per OPINK channel, built once rather than per address.
            const activeByChannel = new Map();
            for (const entry of matrix.entries) {
                if (!entry.isEnabled)
                    continue;
                const list = activeByChannel.get(entry.channelType) ?? [];
                list.push(entry.purpose);
                activeByChannel.set(entry.channelType, list);
            }
            const purposeByCode = new Map(catalogue.map((purpose) => [purpose.code, purpose]));
            const purposeLabel = (code) => {
                const purpose = purposeByCode.get(code);
                return purpose
                    ? resolveConsentPurposeLabel(purpose, locale.value)
                    : t(`consent.matrix.purpose.${code.toLowerCase()}`);
            };
            const purposeKind = (code) => purposeKindOf(purposeByCode, code);
            // Address identity is (type, raw value) — the same key the request used. Never
            // normalizedValue: the read leaves it empty for an address with no consent row yet,
            // which would collide two un-consented addresses of the same type.
            const channelByKey = new Map(contactChannels.map((channel) => [`${channel.type}|${channel.value}`, channel]));
            const nextAddresses = consents.addresses.map((address) => {
                const contactChannel = channelByKey.get(`${address.channelType}|${address.value}`);
                const activePurposes = activeByChannel.get(toConsentChannelType(address.channelType)) ?? [];
                const existingByPurpose = new Map(address.consents.map((consent) => [consent.purpose, consent]));
                const codes = listedPurposeCodes(activePurposes, existingByPurpose.keys(), purposeKind);
                // Refusals dropped, granted purposes first — the shared badge-line rule, applied here and
                // in the consents tab from the same helper so the two screens agree.
                const purposes = toDisplayedPurposes(codes.map((code) => ({
                    code,
                    label: purposeLabel(code),
                    status: (existingByPurpose.get(code)?.status ?? "NotSet"),
                    kind: purposeKind(code),
                })));
                return {
                    addressType: address.channelType,
                    value: address.value,
                    labelKey: contactChannel
                        ? labelKeyFor(contactChannel)
                        : `${LABEL_KEY_PREFIX}.${address.channelType === "Phone" ? "phone" : "email"}`,
                    isPrimary: address.isPrimary,
                    purposes,
                };
            });
            if (currentRequestId !== requestId)
                return;
            addresses.value = nextAddresses;
        }
        catch (e) {
            if (currentRequestId !== requestId)
                return;
            if (axios.isAxiosError(e) && e.response?.status === 403) {
                isForbidden.value = true;
                logger.debug("Consent read forbidden for the current user", e);
            }
            else {
                loadError.value = true;
                logger.error("Failed to load the contact consent overview", e);
            }
        }
        finally {
            if (currentRequestId === requestId)
                isLoading.value = false;
        }
    }
    return { addresses, isLoading, loadError, isForbidden, load };
}
